import { CommonModule } from '@angular/common';

import { Component, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';

import { RouterModule } from '@angular/router';

import { DriveLookupService } from '../../my-account/test-papers/drive-lookup.service';

import { DigitalDownloadService } from '../../common-myaccount-service/digital-download.service';

export interface DigitalResource {

  id: number;

  title: string;

  description: string;

  category: string;

  level: string;

  icon: string;

  colorKey: string;

  fileType: string;

  fileSize: string;

  fileName: string;

  fileId: string;

  fileUrl: string;

  price: number;

  salePrice?: number | null;

  downloads: number;

}

export interface DriveProductMeta {

  fileName: string;

  title: string;

  description: string;

  category: string;

  level: string;

  icon: string;

  colorKey: string;

  price: number;

  salePrice?: number;

  downloads: number;

}

@Component({
  selector: 'app-digital-downloads',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule
  ],
  templateUrl: './digital-downloads.component.html',
  styleUrl: './digital-downloads.component.css',
})
export class DigitalDownloadsComponent {

// =====================================================

  // GOOGLE DRIVE

  // =====================================================

  /**

   * Main folder containing:

   *

   * Digital Downloads

   * ├── Maths

   * ├── English

   * ├── VR

   * ├── NVR

   */

  downloadsFolderId = '18elBWCvKM1tApaMB-MqoEqZ-qYh0wBmz';

  apiKey = 'AIzaSyDR484mbUxUuZfrBH2DkmuUrS4oFXhNT9w';

  // =====================================================

  // RESOURCES

  // =====================================================

  resources: DigitalResource[] = [];
  filteredResources: DigitalResource[] = [];

  // =====================================================

  // FILTERS

  // =====================================================

  categories: string[] = ['All'];
  activeCategory = 'All';
  searchQuery = '';

  // =====================================================

  // MODAL

  // =====================================================

  showModal = false;
  activeTab: 'purchase' | 'existing' = 'purchase';
  selectedResource: DigitalResource | null = null;

  // =====================================================

  // PURCHASE STATE

  // =====================================================

  isProcessingPayment = false;
  paymentCompleted = false;
  downloadUrl = '';
  orderId = '';
  paymentId = '';

  // =====================================================

  // PURCHASE LOOKUP

  // =====================================================

  lookupEmail = '';
  purchaseFound = false;
  purchaseChecked = false;

  // =====================================================

  // FORM

  // =====================================================

  formData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false,
  };

  formErrors: Record<string, string> = {
    firstName: '',
    lastName: '',
    email: '',
  };

  constructor(
    private drive: DriveLookupService,
    private digitalDownloadService: DigitalDownloadService
  ) {}

  // =====================================================

  // INIT

  // =====================================================

  ngOnInit(): void {
    this.loadProductsFromDrive();
  }

  // =====================================================

  // LOAD PRODUCTS

  // =====================================================

  loadProductsFromDrive(): void {

    this.drive

      .listChildFolders(

        this.downloadsFolderId,

        this.apiKey,

        {

          orderBy: 'name'

        }

      )

      .subscribe({
        next: (categoryFolders: any[]) => {
          if (!categoryFolders?.length) {
            console.log(
              'No digital download folders found'
            );
            return;
          }
          categoryFolders.forEach(folder => {
            this.loadCategoryProducts(folder);
          });
        },
        error: (err) => {
          console.error(
            'Failed loading category folders',
            err
          );
        }
      });
  }

  loadCategoryProducts(

    categoryFolder: any

  ): void {

    this.drive

      .findFileByExactName(

        categoryFolder.id,

        'products.json',

        this.apiKey

      )

      .subscribe({

        next: (jsonFile: any) => {

          if (!jsonFile) {

            console.warn(

              'products.json missing in',

              categoryFolder.name

            );

            return;

          }

          this.drive

            .loadJsonFile(

              jsonFile.id,

              this.apiKey

            )

            .subscribe({

              next: (

                productMetaList: DriveProductMeta[]

              ) => {

                this.drive

                  .listFilesInFolder(

                    categoryFolder.id,

                    this.apiKey

                  )

                  .subscribe({

                    next: (files: any[]) => {

                      this.mapProducts(

                        productMetaList,

                        files

                      );

                    },

                    error: (err) => {

                      console.error(

                        'Failed loading files',

                        err

                      );

                    }

                  });

              },

              error: (err) => {

                console.error(

                  'Failed loading products.json',

                  err

                );

              }

            });

        },

        error: (err) => {

          console.error(

            'Failed locating products.json',

            err

          );

        }

      });

  }

  mapProducts(

    productMetaList: DriveProductMeta[],

    files: any[]

  ): void {

    productMetaList.forEach(meta => {

      const file = files.find(f => {

        return (

          f.name &&

          f.name

            .toString()

            .trim()

            .toLowerCase() ===

          meta.fileName

            .toString()

            .trim()

            .toLowerCase()

        );

      });

      if (!file) {

        console.warn(

          'Matching file not found:',

          meta.fileName

        );

        return;

      }

      const resource: DigitalResource = {

        id: this.resources.length + 1,

        title: meta.title,

        description: meta.description,

        category: meta.category,

        level: meta.level,

        icon: meta.icon,

        colorKey: meta.colorKey,

        fileType: this.getFileType(

          meta.fileName

        ),

        fileSize: this.formatBytes(

          Number(file.size || 0)

        ),

        fileName: meta.fileName,

        fileId: file.id,

        fileUrl: this.drive.buildDownloadUrl(

          file.id

        ),

        price: meta.price,

        salePrice: meta.salePrice,

        downloads: meta.downloads || 0,

      };

      this.resources.push(resource);

    });

    this.filteredResources = [
      ...this.resources
    ];
    this.buildCategories();
  }

  // =====================================================

  // CATEGORIES

  // =====================================================

  buildCategories(): void {
    const uniqueCategories: string[] = [];
    this.resources.forEach(resource => {
      if (
        resource.category &&
        !uniqueCategories.includes(
          resource.category
        )
      ) {
        uniqueCategories.push(
          resource.category
        );
      }
    });

    uniqueCategories.sort();
    this.categories = [
      'All',
      ...uniqueCategories
    ];
  }

  // =====================================================

  // FILTERING

  // =====================================================

  setCategory(
    category: string
  ): void {
    this.activeCategory = category;
    this.filterResources();
  }

  filterResources(): void {

    const query =
      this.searchQuery
        .toLowerCase()
        .trim();
    this.filteredResources =
      this.resources.filter(resource => {
        const categoryMatch =
          this.activeCategory === 'All' ||
          resource.category ===
          this.activeCategory;
        const searchMatch =
          query === '' ||
          resource.title
            .toLowerCase()
            .includes(query) ||
          resource.description
            .toLowerCase()
            .includes(query) ||
          resource.category
            .toLowerCase()
            .includes(query) ||
          resource.level
            .toLowerCase()
            .includes(query);
        return (
          categoryMatch &&
          searchMatch
        );
      });
  }

  resetFilters(): void {
    this.activeCategory = 'All';
    this.searchQuery = '';
    this.filteredResources = [
      ...this.resources
    ];

  }

  // =====================================================

  // MODAL

  // =====================================================

  openPurchaseModal(
    resource: DigitalResource
  ): void {
    this.selectedResource = resource;
    this.activeTab = 'purchase';
    this.showModal = true;
    this.paymentCompleted = false;
    this.purchaseFound = false;
    this.purchaseChecked = false;
    this.lookupEmail = '';
    this.downloadUrl = '';
    this.resetForm();
    document.body.style.overflow =
      'hidden';
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedResource = null;
    document.body.style.overflow = '';
  }

  resetForm(): void {

    this.formData = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      consent: false,
    };

    this.formErrors = {
      firstName: '',
      lastName: '',
      email: '',
    };

  }


    // =====================================================
  // VALIDATION
  // =====================================================

  clearError(field: string): void {

    this.formErrors[field] = '';

  }

  validateForm(): boolean {

    let valid = true;

    this.formErrors = {

      firstName: '',

      lastName: '',

      email: '',

    };

    if (!this.formData.firstName.trim()) {

      this.formErrors.firstName =
        'First name is required';

      valid = false;

    }

    if (!this.formData.lastName.trim()) {

      this.formErrors.lastName =
        'Last name is required';

      valid = false;

    }

    if (!this.formData.email.trim()) {

      this.formErrors.email =
        'Email address is required';

      valid = false;

    } else if (
      !this.isValidEmail(
        this.formData.email
      )
    ) {

      this.formErrors.email =
        'Please enter a valid email address';

      valid = false;

    }

    return valid;

  }

  isValidEmail(
    email: string
  ): boolean {

    const re =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return re.test(email);

  }

  // =====================================================
  // CREATE ORDER
  // =====================================================
  //
  // BACKEND WILL BE CONNECTED LATER
  //
  // Flow:
  //
  // User
  // ↓
  // Fill Form
  // ↓
  // Create Order API
  // ↓
  // Cashfree URL
  // ↓
  // Redirect
  //
  // =====================================================

  createOrder(): void {

    if (!this.validateForm()) {
      return;
    }

    if (!this.selectedResource) {
      return;
    }

    this.isProcessingPayment = true;

    const payload = {

  first_name:
    this.formData.firstName,

  last_name:
    this.formData.lastName,

  email:
    this.formData.email,

  phone:
    this.formData.phone,

  consent:
    this.formData.consent ? 1 : 0,

  resource_title:
    this.selectedResource.title,

  resource_category:
    this.selectedResource.category,

  file_name:
    this.selectedResource.fileName,

  file_id:
    this.selectedResource.fileId,

  download_url:
    this.selectedResource.fileUrl,

  amount:
    this.selectedResource.salePrice ||
    this.selectedResource.price

};

    console.log(
      'Create Order Payload',
      payload
    );

    /**
     * ===================================================
     * ACTUAL IMPLEMENTATION LATER
     * ===================================================
     */

    this.digitalDownloadService
  .createOrder(payload)
  .subscribe({

    next: (response) => {

      this.isProcessingPayment = false;

      if (
        response?.data?.payment_url
      ) {

        window.location.href =
          response.data.payment_url;

      }

    },

    error: (error) => {

      this.isProcessingPayment = false;

      console.error(error);

      alert(
        'Unable to create payment order.'
      );

    }

  });

  }

  // =====================================================
  // PAYMENT SUCCESS
  // =====================================================

  paymentSuccess(
    downloadUrl: string
  ): void {

    this.downloadUrl =
      downloadUrl;

    this.paymentCompleted =
      true;

  }

  // =====================================================
  // PURCHASE LOOKUP
  // =====================================================

  checkPurchase(): void {

    if (
      !this.lookupEmail ||
      !this.selectedResource
    ) {

      return;

    }

    this.purchaseChecked = false;

    this.purchaseFound = false;

    /**
     * ============================================
     * ACTUAL API IMPLEMENTATION
     * ============================================
     */

    
    this.digitalDownloadService
  .checkPurchase({

    email:
      this.lookupEmail,

    file_name:
      this.selectedResource.fileName

  })
      .subscribe({

        next: (response) => {

          this.purchaseChecked =
            true;

          this.purchaseFound =
            response.purchased;

          if (
            response.purchased
          ) {

            this.downloadUrl =
              response.download_url;

          }

        },

        error: () => {

          this.purchaseChecked =
            true;

        }

      });
    

    /**
     * TEMP DEMO
     */

    setTimeout(() => {

      this.purchaseChecked =
        true;

      this.purchaseFound = true;

      this.downloadUrl =
        this.selectedResource!.fileUrl;

    }, 800);

  }

  // =====================================================
  // DOWNLOAD TRACKING
  // =====================================================

  trackDownload(): void {

  if (!this.orderId) {
    return;
  }

  this.digitalDownloadService
    .trackDownload({

      order_id: this.orderId

    })
    .subscribe();
this.closeModal();
}

ngOnDestroy(): void {

  document.body.style.overflow = '';

}

  // =====================================================
  // DISCOUNT
  // =====================================================

  getDiscountPercentage(
    originalPrice: number,
    salePrice: number
  ): number {

    if (
      !originalPrice ||
      !salePrice
    ) {

      return 0;

    }

    return Math.round(
      (
        (
          originalPrice -
          salePrice
        ) /
        originalPrice
      ) * 100
    );

  }

  // =====================================================
  // FILE TYPE
  // =====================================================

  getFileType(
    fileName: string
  ): string {

    if (!fileName) {

      return 'FILE';

    }

    const parts =
      fileName.split('.');

    if (
      parts.length < 2
    ) {

      return 'FILE';

    }

    return (
      parts
        .pop()
        ?.toUpperCase() ||
      'FILE'
    );

  }

  // =====================================================
  // FILE SIZE
  // =====================================================

  formatBytes(
    bytes: number
  ): string {

    if (
      !bytes ||
      bytes === 0
    ) {

      return '0 Bytes';

    }

    const k = 1024;

    const sizes = [
      'Bytes',
      'KB',
      'MB',
      'GB'
    ];

    const i = Math.floor(
      Math.log(bytes) /
      Math.log(k)
    );

    return (
      parseFloat(
        (
          bytes /
          Math.pow(k, i)
        ).toFixed(1)
      ) +
      ' ' +
      sizes[i]
    );

  }


}
